import type { ChannelVisibility, Me } from '@novachat/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Hash, Lock, Search, X } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { client } from '../lib/client';
import { errorMessage } from '../lib/errors';
import { useLive } from '../state/live';
import { Avatar } from './Avatar';
import { closeOnBackdrop } from '../lib/dismiss';

/** Opens a channel; every channel has its chat and its call. */
export function CreateRoomDialog({ me, onClose }: { me: Me; onClose: () => void }) {
  const queries = useQueryClient();
  const dialog = useRef<HTMLDialogElement>(null);
  const nameInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<ChannelVisibility>('public');
  const [members, setMembers] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const users = useQuery({ queryKey: ['users'], queryFn: () => client.api.users(), enabled: visibility === 'private' });

  // Native <dialog>: focus trapping, Esc comes from the browser; a backdrop click closes it too (closeOnBackdrop).
  // showModal() focuses the first control (the close button); start in the name field instead.
  useEffect(() => {
    dialog.current?.showModal();
    nameInput.current?.focus();
  }, []);

  const people = (users.data ?? []).filter((u) => u.id !== me.id && u.name.toLocaleLowerCase('tr').includes(filter.toLocaleLowerCase('tr')));

  function toggle(id: string) {
    setMembers((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return setError('Kanala bir ad ver.');
    setBusy(true);
    setError(null);
    try {
      const room = await client.api.createChannel({
        visibility,
        name: name.trim(),
        memberIds: visibility === 'private' ? [...members] : [],
      });
      await queries.invalidateQueries({ queryKey: ['channels'] });
      onClose();
      useLive.getState().select(room.id);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  const option = (value: ChannelVisibility, icon: React.ReactNode, title: string, hint: string) => (
    <label
      className={`flex flex-1 cursor-pointer items-start gap-2.5 rounded-md border p-3 ${
        visibility === value ? 'border-brand-400 bg-brand-400/8' : 'border-line-strong bg-surface-2 hover:border-fg-3'
      }`}
    >
      <input type="radio" name="visibility" value={value} checked={visibility === value} onChange={() => setVisibility(value)} className="sr-only" />
      <span className="mt-0.5 text-fg-2">{icon}</span>
      <span>
        <span className="block text-sm font-semibold">{title}</span>
        <span className="block text-xs leading-normal text-fg-3">{hint}</span>
      </span>
    </label>
  );

  return (
    <dialog onClick={closeOnBackdrop}
      ref={dialog}
      onClose={onClose}
      aria-labelledby="create-room-title"
      className="m-auto w-[min(460px,calc(100vw-32px))] rounded-xl border border-line-strong bg-surface-1 p-0 text-fg-1 backdrop:bg-black/60"
    >
      <form onSubmit={submit} className="flex flex-col gap-4 p-6" noValidate>
        <div className="flex items-center justify-between">
          <h2 id="create-room-title" className="m-0 font-heading text-xl font-bold">
            Kanal oluştur
          </h2>
          <button type="button" aria-label="Kapat" onClick={() => dialog.current?.close()} className="grid size-8 place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-2">
            <X size={18} />
          </button>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-fg-2">Kanal adı</span>
          <input
            ref={nameInput}
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Örn. genel"
            className="h-[42px] rounded-md border border-line-strong bg-surface-2 px-3 text-base placeholder:text-fg-3 focus:border-brand-400 focus:outline-none sm:text-[15px]"
          />
        </label>

        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0 sm:flex-row">
          <legend className="mb-1.5 text-[13px] font-semibold text-fg-2">Kimler görebilir</legend>
          {option('public', <Hash size={18} />, 'Açık kanal', 'NovaChat’teki herkes görür, yazar ve sese katılır.')}
          {option('private', <Lock size={18} />, 'Özel kanal', 'Sadece eklediğin kişiler görür.')}
        </fieldset>

        {visibility === 'private' && (
          <div className="flex flex-col gap-2">
            <span className="text-[13px] font-semibold text-fg-2">Üyeler {members.size > 0 && <span className="text-fg-3">· {members.size} kişi</span>}</span>
            <label className="flex h-9 items-center gap-2 rounded-md border border-line-strong bg-surface-2 px-2.5 text-fg-3 focus-within:border-brand-400">
              <Search size={15} aria-hidden="true" />
              <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Kişi ara" aria-label="Kişi ara" className="min-w-0 flex-1 border-0 bg-transparent text-base text-fg-1 outline-none sm:text-sm" />
            </label>
            <ul className="m-0 max-h-52 list-none overflow-y-auto rounded-md border border-line p-1">
              {users.isPending && <li className="p-2 text-[13px] text-fg-3">Yükleniyor…</li>}
              {!users.isPending && people.length === 0 && (
                <li className="p-2 text-[13px] leading-normal text-fg-3">
                  {filter ? 'Bu adla kimse yok.' : 'Henüz başka kimse yok. Kişiler NovaChat’e ilk girişlerinde burada görünür.'}
                </li>
              )}
              {people.map((person) => (
                <li key={person.id}>
                  <label className="flex cursor-pointer items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm hover:bg-surface-2">
                    <input type="checkbox" checked={members.has(person.id)} onChange={() => toggle(person.id)} className="size-4 accent-brand-500" />
                    <Avatar id={person.id} name={person.name} size={24} />
                    {person.name}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && (
          <p role="alert" className="m-0 rounded-md border border-red-400/40 bg-red-400/10 px-3 py-2 text-[13.5px]">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => dialog.current?.close()} className="nc-press h-10 rounded-md border border-line-strong bg-surface-2 px-4 text-sm font-semibold">
            Vazgeç
          </button>
          <button type="submit" disabled={busy} className="nc-press nc-cta h-10 rounded-md border-0 bg-brand-500 px-4 text-sm font-bold text-white disabled:opacity-60">
            {busy ? 'Oluşturuluyor…' : 'Oluştur'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
